import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';

const REGIONS = ['South', 'East', 'Midwest', 'West'];

// Bracket matchup order: [topSeed, bottomSeed] in display order
const MATCHUP_PAIRS = [[1,16],[8,9],[5,12],[4,13],[6,11],[3,14],[7,10],[2,15]];

// Map seed to matchup index within a region
function seedToMatchupIdx(seed) {
  return MATCHUP_PAIRS.findIndex(([s1, s2]) => s1 === seed || s2 === seed);
}

// Determine which slot (home=top seed, away=bottom seed) a given seed fills
function seedToSlot(seed) {
  const pair = MATCHUP_PAIRS.find(([s1, s2]) => s1 === seed || s2 === seed);
  if (!pair) return 'away';
  return pair[0] === seed ? 'home' : 'away';
}

// Build the R64 game ID for a region + seed
function getR64GameId(region, seed) {
  const idx = seedToMatchupIdx(seed);
  if (idx === -1) return '';
  return `r64-${region[0].toLowerCase()}${idx + 1}`;
}

// Helper component to show Play-In slot label — avoids IIFE in JSX
function PlayInSlotLabel({ pg, savedGames }) {
  const playInGame = savedGames[pg?.id];
  const winner = playInGame?.winner;
  const winnerTeam = winner && playInGame
    ? (playInGame.homeTeam?.id === winner ? playInGame.homeTeam
      : playInGame.awayTeam?.id === winner ? playInGame.awayTeam : null)
    : null;
  return (
    <span style={{ flex: 1, fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', padding: '7px 12px',
      color: winnerTeam ? 'var(--green)' : 'var(--gray)', fontStyle: winnerTeam ? 'normal' : 'italic' }}>
      {winnerTeam
        ? `✓ ${winnerTeam.name} (Play-In Winner)`
        : `Play-In: ${pg?.team1 || '?'} vs ${pg?.team2 || '?'}`}
    </span>
  );
}

export default function AdminSetupPage() {
  const api = useApi();
  const [activeTab, setActiveTab] = useState('regionorder');
  const [verifyResult, setVerifyResult] = useState(null);
  const [resetR4Status, setResetR4Status] = useState('');
  const [savedGames, setSavedGames] = useState({});
  const [existingSetup, setExistingSetup] = useState(false); // true if data already in Firestore
  const [setupLoaded, setSetupLoaded] = useState(false);     // true once we've tried to load
  const [confirmOverwrite, setConfirmOverwrite] = useState(false); // gameId -> game doc from Firestore
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');
  // ESPN teams cache state
  const [espnCacheStatus, setEspnCacheStatus] = useState(null);
  const [espnCacheUpdating, setEspnCacheUpdating] = useState(false);
  const [espnCacheMsg, setEspnCacheMsg] = useState('');
  const [espnCacheTeams, setEspnCacheTeams] = useState([]);
  const [espnCacheSearch, setEspnCacheSearch] = useState('');
  const [espnCacheSort, setEspnCacheSort] = useState({ col: 'name', dir: 'asc' });

  // ESPN auto-seed state
  const [espnSeeding, setEspnSeeding] = useState(false);
  const [espnSeedStatus, setEspnSeedStatus] = useState('');
  const [espnSeedError, setEspnSeedError] = useState('');
  const [espnPreview, setEspnPreview] = useState(null);

  // Load Bracket modal state
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [loadYear, setLoadYear] = useState(new Date().getFullYear());
  const [loadBracketRunning, setLoadBracketRunning] = useState(false);
  const [loadBracketError, setLoadBracketError] = useState('');
  const [loadBracketPreview, setLoadBracketPreview] = useState(null);
  const [loadMode, setLoadMode] = useState('espn'); // 'espn' | 'json'
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');

  // ESPN teams for fuzzy matching in load modal
  const [espnTeams, setEspnTeams] = useState([]);
  const [espnLoaded, setEspnLoaded] = useState(false);
  const [espnLoading, setEspnLoading] = useState(false);
  // Tools state
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationError, setMigrationError] = useState('');

  // Play-In games — 4 games, each with two teams and a destination region+seed
  const [playInGames, setPlayInGames] = useState([
    { id: 'playin-1', team1: '', team1Id: '', team2: '', team2Id: '', destRegion: 'South',   destSeed: 16 },
    { id: 'playin-2', team1: '', team1Id: '', team2: '', team2Id: '', destRegion: 'Midwest', destSeed: 16 },
    { id: 'playin-3', team1: '', team1Id: '', team2: '', team2Id: '', destRegion: 'West',    destSeed: 16 },
    { id: 'playin-4', team1: '', team1Id: '', team2: '', team2Id: '', destRegion: 'East',    destSeed: 11 },
  ]);

  // Teams: { region: { seed: { name, id } } }
  const [teamData, setTeamData] = useState(() => {
    const d = {};
    REGIONS.forEach(r => {
      d[r] = {};
      for (let s = 1; s <= 16; s++) d[r][s] = { name: '', id: '' };
    });
    return d;
  });

  // Final Four pairings — always Region 1 vs 4, Region 2 vs 3
  // Derived from regionOrder, no manual selection needed

  // Region display order — user can drag/reorder for PDF layout
  const [regionOrder, setRegionOrder] = useState(['South', 'East', 'Midwest', 'West']);

  // Seed entry order toggle
  const [seedEntryOrder, setSeedEntryOrder] = useState('seed'); // 'seed' | 'bracket'
  const SEED_ORDER   = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16];
  const BRACKET_ORDER = [1,16,8,9,5,12,4,13,6,11,3,14,7,10,2,15];

  function moveRegion(idx, dir) {
    const newOrder = [...regionOrder];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    setRegionOrder(newOrder);
    // Immediately persist just the regionOrder to wiring config
    api.saveBracketWiring({ regionOrder: newOrder }).catch(err => console.warn('Region order save:', err.message));
  }

  // Load existing setup from Firestore on mount
  useEffect(() => {
    api.getEspnTeamsCacheStatus().catch(() => null).then(s => { if (s) setEspnCacheStatus(s); });
    Promise.all([
      api.getBracketWiring().catch(() => null),
      api.adminGetGames().catch(() => []),
      api.getAllTeams().catch(() => []),
    ]).then(([wiring, games, teams]) => {
      const hasData = (Array.isArray(teams) && teams.length > 0) ||
                      (Array.isArray(games) && games.filter(g => g.round === 'R64').length > 0);
      setExistingSetup(hasData);

      if (wiring && wiring.playIn && wiring.playIn.length > 0) {
        // Restore Play-In config from wiring
        setPlayInGames(wiring.playIn.map((p, i) => {
          // Reverse-engineer destRegion and destSeed from feedsIntoGameId
          const gameId = p.feedsIntoGameId || '';
          const regionLetter = gameId.replace(/r64-([a-z]).*/, '$1');
          const regionMap = { s: 'South', e: 'East', m: 'Midwest', w: 'West' };
          const destRegion = regionMap[regionLetter] || 'South';
          const gameIdx = parseInt(gameId.replace(/r64-[a-z]/, '')) - 1;
          const pair = [[1,16],[8,9],[5,12],[4,13],[6,11],[3,14],[7,10],[2,15]][gameIdx] || [11, 16];
          const destSeed = p.feedsIntoSlot === 'home' ? pair[0] : pair[1];
          // Find team names from saved games
          const savedGame = Array.isArray(games) ? games.find(g => g.id === p.id) : null;
          return {
            id: p.id || `playin-${i+1}`,
            team1: savedGame?.homeTeam?.id !== 'tbd' ? savedGame?.homeTeam?.name || '' : '',
            team1Id: savedGame?.homeTeam?.id || '',
            team2: savedGame?.awayTeam?.id !== 'tbd' ? savedGame?.awayTeam?.name || '' : '',
            team2Id: savedGame?.awayTeam?.id || '',
            destRegion,
            destSeed,
          };
        }));
      }

      if (wiring && wiring.regionOrder && wiring.regionOrder.length === 4) {
        setRegionOrder(wiring.regionOrder);
      }

      if (Array.isArray(teams) && teams.length > 0) {
        // Restore team data
        const restored = {};
        ['South', 'East', 'Midwest', 'West'].forEach(r => {
          restored[r] = {};
          for (let s = 1; s <= 16; s++) restored[r][s] = { name: '', id: '' };
        });
        teams.forEach(t => {
          if (restored[t.region] && t.seed >= 1 && t.seed <= 16) {
            // Only set if not already set (Play-In teams appear twice)
            if (!restored[t.region][t.seed].name) {
              restored[t.region][t.seed] = { name: t.name, id: t.id };
            }
          }
        });
        setTeamData(restored);
      }
    }).finally(() => setSetupLoaded(true));
  }, []);

  // ESPN team lookup (state declared above in modal state block)
  const [teamSearch, setTeamSearch] = useState({}); // { 'South-1': 'duke', ... }
  const [teamDropdown, setTeamDropdown] = useState(null); // key of open dropdown — teams or playin
  const [highlightedIdx, setHighlightedIdx] = useState(-1);

  async function loadEspnTeams() {
    if (espnLoaded || espnLoading) return espnTeams;
    setEspnLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const resp = await fetch(`${apiBase}/espn-teams`);
      const data = await resp.json();
      const teams = data.teams ? Object.entries(data.teams).map(([id, t]) => ({ id, ...t })) : [];
      setEspnTeams(teams);
      setEspnLoaded(true);
      return teams;
    } catch (err) {
      console.warn('ESPN teams load failed:', err.message);
      return [];
    } finally {
      setEspnLoading(false);
    }
  }

  async function fetchEspnTeamsDirect() {
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const resp = await fetch(`${apiBase}/espn-teams`);
      const data = await resp.json();
      return data.teams ? Object.entries(data.teams).map(([id, t]) => ({ id, ...t })) : [];
    } catch (e) { return []; }
  }

  function fuzzyMatchTeam(name, espnTeamsList) {
    if (!name || !espnTeamsList?.length) return null;
    const normalize = s => (s || '').toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\b(university|college|state|the|of|at|st)\b/g, '')
      .replace(/\s+/g, ' ').trim();
    const n = normalize(name);
    let match = espnTeamsList.find(t =>
      normalize(t.name) === n ||
      normalize(t.shortName || '') === n ||
      normalize(t.abbrev || '') === n
    );
    if (match) return match;
    match = espnTeamsList.find(t =>
      normalize(t.name).includes(n) || n.includes(normalize(t.name)) ||
      (t.shortName && (normalize(t.shortName).includes(n) || n.includes(normalize(t.shortName))))
    );
    if (match) return match;
    const nWords = new Set(n.split(' ').filter(w => w.length > 2));
    let bestScore = 0, bestMatch = null;
    for (const t of espnTeamsList) {
      const tWords = new Set(normalize(t.name).split(' ').filter(w => w.length > 2));
      const overlap = [...nWords].filter(w => tWords.has(w)).length;
      if (overlap > bestScore) { bestScore = overlap; bestMatch = t; }
    }
    return bestScore >= 1 ? bestMatch : null;
  }

  // ── Load Bracket (ESPN API) ─────────────────────────────────────────────
  async function fetchLoadBracketPreview() {
    setLoadBracketRunning(true); setLoadBracketError(''); setLoadBracketPreview(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const token = await (async () => {
        try { const { getAuth } = await import('firebase/auth'); return await getAuth().currentUser?.getIdToken(); }
        catch { return null; }
      })();
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) };
      const roParam = regionOrder.join(',');
      const res = await fetch(`${apiBase}/admin/setup/espn-seed?year=${loadYear}&regionOrder=${roParam}`, { method: 'GET', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch bracket');
      setLoadBracketPreview(data);
    } catch (e) { setLoadBracketError(e.message); }
    finally { setLoadBracketRunning(false); }
  }

  // ── Load Bracket (JSON Import) ──────────────────────────────────────────
  function handleJsonFileUpload(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => { setJsonText(e.target.result); setJsonError(''); };
    reader.readAsText(file);
  }

  async function parseJsonBracket() {
    let parsed;
    try {
      const text = jsonText.trim();
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      parsed = JSON.parse(jsonMatch?.[0] || text);
    } catch (e) { setJsonError('Invalid JSON — ' + e.message); return; }
    if (!parsed.regions) { setJsonError('JSON must have a "regions" object with South, East, Midwest, West keys.'); return; }

    setLoadBracketRunning(true);
    try {
      let espnTeamsList = espnTeams;
      if (!espnLoaded || espnTeamsList.length === 0) {
        espnTeamsList = await fetchEspnTeamsDirect();
        if (espnTeamsList.length > 0) { setEspnTeams(espnTeamsList); setEspnLoaded(true); }
      }

      const MATCHUP_PAIRS = [[1,16],[8,9],[5,12],[4,13],[6,11],[3,14],[7,10],[2,15]];
      const REGIONS = ['South', 'East', 'Midwest', 'West'];
      const tbd = { id: 'tbd', name: 'TBD', abbrev: 'TBD', seed: 0, score: 0 };
      const teams = [];
      const teamsSeen = new Set();
      const unmatchedTeams = [];
      const regionTeams = {};

      for (const [region, regionList] of Object.entries(parsed.regions)) {
        regionTeams[region] = [];
        for (const entry of (regionList || [])) {
          if (!entry.name) continue;

          let team;
          if (entry.espnId) {
            // espnId provided — trust it completely, use name from JSON
            const abbrev = entry.name.split(' ').slice(-1)[0].substring(0, 4).toUpperCase();
            team = { id: String(entry.espnId), name: entry.name, abbrev, seed: entry.seed, region };
          } else {
            // No espnId — fuzzy match against cache
            const espnMatch = fuzzyMatchTeam(entry.name, espnTeamsList);
            if (espnMatch) {
              team = { id: espnMatch.id, name: espnMatch.name, abbrev: espnMatch.abbreviation || espnMatch.abbrev || espnMatch.shortName, seed: entry.seed, region };
            } else {
              unmatchedTeams.push(`${entry.name} (${region} seed ${entry.seed})`);
              team = { id: `unknown-${entry.name.toLowerCase().replace(/\s+/g,'-')}`, name: entry.name, abbrev: entry.name.substring(0,4).toUpperCase(), seed: entry.seed, region };
            }
          }
          if (!teamsSeen.has(`${region}-${team.id}`)) { teamsSeen.add(`${region}-${team.id}`); teams.push(team); }
          regionTeams[region].push(team);
        }
      }

      const parsedPlayIn = (parsed.playIn || []).map((pg, idx) => {
        const id = `playin-${idx + 1}`;
        const t1 = pg.team1EspnId
          ? { id: String(pg.team1EspnId), name: pg.team1, abbrev: pg.team1?.split(' ').slice(-1)[0].substring(0,4).toUpperCase() }
          : fuzzyMatchTeam(pg.team1, espnTeamsList);
        const t2 = pg.team2EspnId
          ? { id: String(pg.team2EspnId), name: pg.team2, abbrev: pg.team2?.split(' ').slice(-1)[0].substring(0,4).toUpperCase() }
          : fuzzyMatchTeam(pg.team2, espnTeamsList);
        const region = pg.region || 'South';
        const destSeed = pg.seed || 16;
        const rl = region[0].toLowerCase();
        const pairIdx = MATCHUP_PAIRS.findIndex(([s1, s2]) => s1 === destSeed || s2 === destSeed);
        const feedsIntoGameId = pairIdx >= 0 ? `r64-${rl}${pairIdx + 1}` : `r64-${rl}1`;
        const pair = MATCHUP_PAIRS[pairIdx] || [16, 16];
        const feedsIntoSlot = pair[0] === destSeed ? 'home' : 'away';
        if (t1) teams.push({ id: t1.id, name: t1.name, abbrev: t1.abbreviation || t1.abbrev, seed: destSeed, region });
        if (t2) teams.push({ id: t2.id, name: t2.name, abbrev: t2.abbreviation || t2.abbrev, seed: destSeed, region });
        if (!t1) unmatchedTeams.push(`${pg.team1} (Play-In)`);
        if (!t2) unmatchedTeams.push(`${pg.team2} (Play-In)`);
        return {
          id, feedsIntoGameId, feedsIntoSlot, seedLine: destSeed, region,
          homeTeam: t1 ? { id: t1.id, name: t1.name, abbrev: t1.abbreviation || t1.abbrev, seed: destSeed, score: 0 } : { ...tbd, seed: destSeed },
          awayTeam: t2 ? { id: t2.id, name: t2.name, abbrev: t2.abbreviation || t2.abbrev, seed: destSeed, score: 0 } : { ...tbd, seed: destSeed },
          // Form state shape for setPlayInGames
          formState: {
            id, destRegion: region, destSeed,
            team1: t1?.name || pg.team1 || '', team1Id: t1?.id || '',
            team2: t2?.name || pg.team2 || '', team2Id: t2?.id || '',
          },
        };
      });

      const games = [];
      parsedPlayIn.forEach(pg => {
        games.push({ id: pg.id, round: 'playin', region: null, status: 'scheduled', shortName: `${pg.homeTeam.name} vs ${pg.awayTeam.name}`, homeTeam: pg.homeTeam, awayTeam: pg.awayTeam, winner: null, date: '', feedsIntoGameId: pg.feedsIntoGameId, feedsIntoSlot: pg.feedsIntoSlot });
      });
      const builtPlayIn = parsedPlayIn.map(pg => ({ id: pg.id, seedLine: pg.seedLine, feedsIntoGameId: pg.feedsIntoGameId, feedsIntoSlot: pg.feedsIntoSlot }));
      const fullPlayIn = parsedPlayIn.map(pg => pg.formState);

      REGIONS.forEach(region => {
        const rTeams = regionTeams[region] || [];
        const teamBySeed = {};
        rTeams.forEach(t => { teamBySeed[t.seed] = t; });
        const l = region[0].toLowerCase();
        MATCHUP_PAIRS.forEach(([s1, s2], idx) => {
          const gameId = `r64-${l}${idx + 1}`;
          const pg1 = builtPlayIn.find(p => p.feedsIntoGameId === gameId && p.feedsIntoSlot === 'home');
          const pg2 = builtPlayIn.find(p => p.feedsIntoGameId === gameId && p.feedsIntoSlot === 'away');
          const t1 = teamBySeed[s1]; const t2 = teamBySeed[s2];
          games.push({
            id: gameId, round: 'R64', region, status: 'scheduled', shortName: `${region} ${s1} vs ${s2}`,
            homeTeam: pg1 ? { id: `${pg1.id}-winner`, name: 'Play-In Winner', abbrev: 'TBD', seed: s1, score: 0, playInGameId: pg1.id } : t1 ? { id: t1.id, name: t1.name, abbrev: t1.abbrev, seed: t1.seed, score: 0 } : { ...tbd, seed: s1 },
            awayTeam: pg2 ? { id: `${pg2.id}-winner`, name: 'Play-In Winner', abbrev: 'TBD', seed: s2, score: 0, playInGameId: pg2.id } : t2 ? { id: t2.id, name: t2.name, abbrev: t2.abbrev, seed: t2.seed, score: 0 } : { ...tbd, seed: s2 },
            winner: null, date: '',
          });
        });
        for (let i = 1; i <= 4; i++) games.push({ id: `r32-${l}${i}`, round: 'R32', region, status: 'scheduled', shortName: 'TBD', homeTeam: tbd, awayTeam: tbd, winner: null, date: '' });
        for (let i = 1; i <= 2; i++) games.push({ id: `r16-${l}${i}`, round: 'R16', region, status: 'scheduled', shortName: 'TBD', homeTeam: tbd, awayTeam: tbd, winner: null, date: '' });
        games.push({ id: `r8-${l}`, round: 'R8', region, status: 'scheduled', shortName: 'TBD', homeTeam: tbd, awayTeam: tbd, winner: null, date: '' });
      });

      const [s1a, s1b] = [regionOrder[0], regionOrder[3]];
      const [s2a, s2b] = [regionOrder[1], regionOrder[2]];
      games.push({ id: 'r4-1', round: 'R4', region: null, status: 'scheduled', shortName: `${s1a} vs ${s1b}`, homeTeam: { ...tbd, name: `${s1a} Champion` }, awayTeam: { ...tbd, name: `${s1b} Champion` }, winner: null, date: '', semifinalRegions: [s1a, s1b] });
      games.push({ id: 'r4-2', round: 'R4', region: null, status: 'scheduled', shortName: `${s2a} vs ${s2b}`, homeTeam: { ...tbd, name: `${s2a} Champion` }, awayTeam: { ...tbd, name: `${s2b} Champion` }, winner: null, date: '', semifinalRegions: [s2a, s2b] });
      games.push({ id: 'final-1', round: 'Final', region: null, status: 'scheduled', shortName: 'Championship', homeTeam: { ...tbd, name: 'Semifinal 1 Winner' }, awayTeam: { ...tbd, name: 'Semifinal 2 Winner' }, winner: null, date: '' });

      const regionCounts = {};
      REGIONS.forEach(r => {
        const mainTeams = (regionTeams[r] || []).length;
        const playInSlots = parsedPlayIn.filter(pg => pg.region === r).length;
        regionCounts[r] = mainTeams + playInSlots;
      });

      setLoadBracketPreview({
        teams, games, regionOrder,
        playIn: builtPlayIn,
        fullPlayIn,
        year: parsed.year || loadYear,
        unmatched: unmatchedTeams,
        summary: { totalTeams: teams.length, totalGames: games.length, playInCount: parsedPlayIn.length, regionCounts },
      });
    } catch (e) { setLoadBracketError(e.message); }
    finally { setLoadBracketRunning(false); }
  }

  // ── Apply loaded bracket to Firestore ──────────────────────────────────
  async function applyLoadBracket() {
    if (!loadBracketPreview) return;
    setLoadBracketRunning(true); setLoadBracketError('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const token = await (async () => {
        try { const { getAuth } = await import('firebase/auth'); return await getAuth().currentUser?.getIdToken(); }
        catch { return null; }
      })();
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) };
      await fetch(`${apiBase}/admin/setup/teams`, { method: 'POST', headers, body: JSON.stringify({ teams: loadBracketPreview.teams }) });
      await fetch(`${apiBase}/admin/setup/games`, { method: 'POST', headers, body: JSON.stringify({ games: loadBracketPreview.games }) });
      const derivedFinalFour = { semi1: [regionOrder[0], regionOrder[3]], semi2: [regionOrder[1], regionOrder[2]] };
      await api.saveBracketWiring({ regionOrder, finalFour: derivedFinalFour, playIn: loadBracketPreview.playIn || [] });

      // Update component state
      if (loadBracketPreview.fullPlayIn?.length > 0) setPlayInGames(loadBracketPreview.fullPlayIn);
      const newTeamData = {};
      ['South','East','Midwest','West'].forEach(r => {
        newTeamData[r] = {};
        for (let s = 1; s <= 16; s++) newTeamData[r][s] = { name: '', id: '' };
      });
      loadBracketPreview.teams.forEach(t => {
        if (t.region && ['South','East','Midwest','West'].includes(t.region) && t.seed >= 1 && t.seed <= 16) {
          newTeamData[t.region][t.seed] = { name: t.name, id: t.id };
        }
      });
      setTeamData(newTeamData);

      setShowLoadModal(false);
      setLoadBracketPreview(null);
      setLoadBracketError('');
      setJsonText('');
      setJsonError('');
      setExistingSetup(true);
      setSaveStatus(`✅ ${loadBracketPreview.year} bracket loaded — ${loadBracketPreview.summary?.totalTeams || 0} teams, ${loadBracketPreview.summary?.totalGames || 0} games, ${loadBracketPreview.summary?.playInCount || 0} First Four games. Review the tabs below to verify everything looks correct, then go to Verify & Save when ready.`);
      setActiveTab('playin');
    } catch (e) { setLoadBracketError(e.message); }
    finally { setLoadBracketRunning(false); }
  }

  async function loadEspnCacheTeams() {
    if (espnCacheTeams.length > 0) return;
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const resp = await fetch(`${apiBase}/espn-teams`);
      const data = await resp.json();
      if (data.teams) {
        const list = Object.entries(data.teams).map(([id, t]) => ({
          id, name: t.name || t.displayName || '', abbrev: t.abbrev || t.abbreviation || '',
        })).sort((a, b) => a.name.localeCompare(b.name));
        setEspnCacheTeams(list);
        setEspnCacheStatus(prev => ({ ...prev, count: list.length }));
      }
    } catch (e) { console.warn('ESPN cache load:', e.message); }
  }


  function getEspnMatches(query) {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return espnTeams
      .filter(t => t.name.toLowerCase().includes(q) || t.abbrev.toLowerCase().includes(q) || (t.shortName && t.shortName.toLowerCase().includes(q)))
      .slice(0, 8);
  }

  function autoId(name) {
    return name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-').substring(0, 30);
  }

  function setTeam(region, seed, field, value) {
    setTeamData(d => ({ ...d, [region]: { ...d[region], [seed]: { ...d[region][seed], [field]: value } } }));
  }

  function updatePlayIn(idx, field, value) {
    setPlayInGames(g => g.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  }

  // Which seeds are Play-In slots (derived from playInGames config)
  function getPlayInSeeds() {
    const slots = {};
    playInGames.forEach(pg => {
      if (!slots[pg.destRegion]) slots[pg.destRegion] = new Set();
      slots[pg.destRegion].add(pg.destSeed);
    });
    return slots;
  }

  async function handleSaveAll(force = false) {
    if (existingSetup && !force && !confirmOverwrite) {
      setConfirmOverwrite(true);
      return;
    }
    setConfirmOverwrite(false);
    setError(''); setSaveStatus('Saving...');
    try {
      const { teams, games } = buildBracketData();
      if (!teams.length) { setError('No teams entered yet'); setSaveStatus(''); return; }
      await api.post('/admin/setup/teams', { teams });
      await api.post('/admin/setup/games', { games });

      // Save wiring config — Final Four always Region 1v4, Region 2v3
      const derivedFinalFour = {
        semi1: [regionOrder[0], regionOrder[3]],
        semi2: [regionOrder[1], regionOrder[2]],
      };
      await api.saveBracketWiring({
        regionOrder,
        finalFour: derivedFinalFour,
        playIn: playInGames.map(pg => ({
          id: pg.id,
          seedLine: pg.destSeed,
          feedsIntoGameId: getR64GameId(pg.destRegion, pg.destSeed),
          feedsIntoSlot: seedToSlot(pg.destSeed),
        })),
      });

      setSaveStatus(`✅ Saved ${teams.length} teams, ${games.length} games, and bracket wiring!`);
      setExistingSetup(true);
    } catch (err) { setError(err.message); setSaveStatus(''); }
  }

  function buildBracketData() {
    const teams = [];
    const games = [];
    const playInSlots = getPlayInSeeds();

    // Build teams (skip Play-In slots — those come from Play-In winners)
    REGIONS.forEach(region => {
      for (let seed = 1; seed <= 16; seed++) {
        const t = teamData[region][seed];
        const isPlayInSlot = playInSlots[region]?.has(seed);
        if (t.name && !isPlayInSlot) {
          teams.push({
            id: t.id || autoId(t.name),
            name: t.name,
            abbrev: t.name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 4),
            seed, region,
          });
        }
        // Also add Play-In teams as teams
        if (isPlayInSlot) {
          const pg = playInGames.find(p => p.destRegion === region && p.destSeed === seed);
          if (pg?.team1) {
            const t1id = autoId(pg.team1);
            teams.push({ id: t1id, name: pg.team1, abbrev: pg.team1.split(' ').map(w=>w[0]).join('').toUpperCase().substring(0,4), seed, region });
          }
          if (pg?.team2) {
            const t2id = autoId(pg.team2);
            if (!teams.find(t => t.id === t2id)) {
              teams.push({ id: t2id, name: pg.team2, abbrev: pg.team2.split(' ').map(w=>w[0]).join('').toUpperCase().substring(0,4), seed, region });
            }
          }
        }
      }
    });

    const teamByRegionSeed = {};
    teams.forEach(t => { teamByRegionSeed[`${t.region}-${t.seed}`] = t; });

    // Build Play-In games
    playInGames.forEach(pg => {
      if (!pg.team1 || !pg.team2) return;
      const t1 = teams.find(t => t.name === pg.team1);
      const t2 = teams.find(t => t.name === pg.team2);
      if (!t1 || !t2) return;
      games.push({
        id: pg.id, round: 'playin', region: null, status: 'scheduled',
        shortName: `${t1.name} vs ${t2.name}`,
        homeTeam: { id: t1.id, name: t1.name, abbrev: t1.abbrev, seed: t1.seed, score: 0 },
        awayTeam: { id: t2.id, name: t2.name, abbrev: t2.abbrev, seed: t2.seed, score: 0 },
        winner: null, date: '',
        feedsIntoGameId: getR64GameId(pg.destRegion, pg.destSeed),
        feedsIntoSlot: seedToSlot(pg.destSeed),
      });
    });

    // Build R64 games
    REGIONS.forEach(region => {
      MATCHUP_PAIRS.forEach(([s1, s2], idx) => {
        const gameId = `r64-${region[0].toLowerCase()}${idx + 1}`;
        const isPlayInHome = playInGames.some(p => p.destRegion === region && p.destSeed === s1);
        const isPlayInAway = playInGames.some(p => p.destRegion === region && p.destSeed === s2);

        const getSlot = (seed, isPlayIn) => {
          if (isPlayIn) {
            const pg = playInGames.find(p => p.destRegion === region && p.destSeed === seed);
            return {
              id: `${pg?.id || 'playin'}-winner`,
              name: pg ? `Play-In Winner (${pg.team1 || '?'} vs ${pg.team2 || '?'})` : 'Play-In Winner',
              abbrev: 'TBD', seed, score: 0,
              playInGameId: pg?.id,
            };
          }
          const t = teamByRegionSeed[`${region}-${seed}`];
          return t ? { id: t.id, name: t.name, abbrev: t.abbrev, seed: t.seed, score: 0 }
                   : { id: 'tbd', name: 'TBD', abbrev: 'TBD', seed, score: 0 };
        };

        games.push({
          id: gameId, round: 'R64', region, status: 'scheduled',
          shortName: `${region} ${s1} vs ${s2}`,
          homeTeam: getSlot(s1, isPlayInHome),
          awayTeam: getSlot(s2, isPlayInAway),
          winner: null, date: '',
        });
      });

      // R32, R16, R8 placeholders
      const l = region[0].toLowerCase();
      const tbd = { id: 'tbd', name: 'TBD', abbrev: 'TBD', seed: 0, score: 0 };
      for (let i = 1; i <= 4; i++) games.push({ id: `r32-${l}${i}`, round: 'R32', region, status: 'scheduled', shortName: 'TBD', homeTeam: tbd, awayTeam: tbd, winner: null, date: '' });
      for (let i = 1; i <= 2; i++) games.push({ id: `r16-${l}${i}`, round: 'R16', region, status: 'scheduled', shortName: 'TBD', homeTeam: tbd, awayTeam: tbd, winner: null, date: '' });
      games.push({ id: `r8-${l}`, round: 'R8', region, status: 'scheduled', shortName: 'TBD', homeTeam: tbd, awayTeam: tbd, winner: null, date: '' });
    });

    // Final Four & Championship — always Region 1v4, Region 2v3
    const [s1a, s1b] = [regionOrder[0], regionOrder[3]];
    const [s2a, s2b] = [regionOrder[1], regionOrder[2]];
    const tbd = { id: 'tbd', name: 'TBD', abbrev: 'TBD', seed: 0, score: 0 };
    games.push({ id: 'r4-1', round: 'R4', region: null, status: 'scheduled', shortName: `${s1a} vs ${s1b}`,
      homeTeam: { ...tbd, name: `${s1a} Champion` }, awayTeam: { ...tbd, name: `${s1b} Champion` },
      winner: null, date: '', semifinalRegions: [s1a, s1b] });
    games.push({ id: 'r4-2', round: 'R4', region: null, status: 'scheduled', shortName: `${s2a} vs ${s2b}`,
      homeTeam: { ...tbd, name: `${s2a} Champion` }, awayTeam: { ...tbd, name: `${s2b} Champion` },
      winner: null, date: '', semifinalRegions: [s2a, s2b] });
    games.push({ id: 'final-1', round: 'Final', region: null, status: 'scheduled', shortName: 'Championship',
      homeTeam: { ...tbd, name: 'Semifinal 1 Winner' }, awayTeam: { ...tbd, name: 'Semifinal 2 Winner' },
      winner: null, date: '' });

    return { teams, games };
  }

  async function handleResetR4() {
    setResetR4Status('Resetting...');
    try {
      const result = await api.post('/admin/setup/reset-r4', {});
      setResetR4Status(`✅ Reset: ${result.r4_1} / ${result.r4_2}`);
    } catch (err) {
      setResetR4Status('❌ ' + err.message);
    }
  }

  async function handleVerify() {
    setError(''); setVerifyResult(null);
    try { setVerifyResult(await api.runVerify()); }
    catch (err) { setError(err.message); }
  }


  // ── ESPN Auto-Seed ────────────────────────────────────────────────────────
  async function fetchEspnBracket() {
    setEspnSeeding(true); setEspnSeedError(''); setEspnSeedStatus(''); setEspnPreview(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const token = await (async () => {
        try { const { getAuth } = await import('firebase/auth'); return await getAuth().currentUser?.getIdToken(); }
        catch { return null; }
      })();
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) };
      const res = await fetch(apiBase + '/admin/setup/espn-seed', { method: 'GET', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'ESPN seed failed');
      setEspnPreview(data);
      setEspnSeedStatus('Preview loaded — review below then click Save to apply.');
    } catch (e) { setEspnSeedError(e.message); }
    finally { setEspnSeeding(false); }
  }

  async function applyEspnSeed() {
    if (!espnPreview) return;
    setEspnSeeding(true); setEspnSeedError('');
    try {
      await api.post('/admin/setup/teams', { teams: espnPreview.teams });
      await api.post('/admin/setup/games', { games: espnPreview.games });
      const derivedFinalFour = { semi1: [regionOrder[0], regionOrder[3]], semi2: [regionOrder[1], regionOrder[2]] };
      await api.saveBracketWiring({ regionOrder: espnPreview.regionOrder || regionOrder, finalFour: derivedFinalFour, playIn: espnPreview.playIn || [] });
      setEspnSeedStatus('ESPN bracket saved successfully! Go to Verify & Save to confirm.');
      setEspnPreview(null);
      setExistingSetup(true);
    } catch (e) { setEspnSeedError(e.message); }
    finally { setEspnSeeding(false); }
  }

  // ── Sleeper ID Migration ──────────────────────────────────────────────────
  async function runSleeperMigration(dryRun = true) {
    setMigrationRunning(true); setMigrationError(''); setMigrationResult(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const token = await (async () => {
        try { const { getAuth } = await import('firebase/auth'); return await getAuth().currentUser?.getIdToken(); }
        catch { return null; }
      })();
      const res = await fetch(apiBase + '/admin/tools/fix-sleeper-ids?dryRun=' + dryRun, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Migration failed');
      setMigrationResult({ ...data, dryRun });
    } catch (e) { setMigrationError(e.message); }
    finally { setMigrationRunning(false); }
  }

  const playInSlotMap = getPlayInSeeds();

  const tabs = [
    ['regionorder', '1. Region Order'],
    ['playin',      '2. Play-In Games'],
    ['teams',       '3. Enter Teams'],
    ['finalfour',   '4. Final Four'],
    ['verify',      '5. Verify & Save'],
    ['espn',        '6. ESPN Auto-Seed'],
    ['tools',       '7. Tools'],
    ['espncache',   '8. ESPN Teams'],
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--blue)', letterSpacing: '2px' }}>BRACKET SETUP</h2>
        <span className="badge badge-orange">Selection Sunday Tool</span>
      </div>
      <div className="alert alert-info" style={{ marginBottom: 20 }}>
        <strong>Workflow:</strong> Set Region Order → click <strong>Load Bracket</strong> to auto-load from ESPN, or manually configure Play-In games → Enter Teams → Set Final Four → Verify & Save
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {saveStatus && <div className="alert alert-success">{saveStatus}</div>}

      <div className="tabs" style={{ marginBottom: 24 }}>
        {tabs.map(([t, l]) => (
          <button key={t} className={`tab${activeTab === t ? ' active' : ''}`} onClick={() => { setActiveTab(t); if (t === 'espncache') loadEspnCacheTeams(); }}>{l}</button>
        ))}
      </div>

      {/* ── 1. PLAY-IN ────────────────────────────────────────── */}
      {activeTab === 'playin' && (
        <div>
          <div className="alert alert-info" style={{ marginBottom: 20 }}>
            Enter the 4 First Four games. For each game, specify the two teams playing and which
            <strong> Region</strong> and <strong>Seed</strong> the winner advances to in the main bracket.
            The game slot is calculated automatically.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 20 }}>
            {playInGames.map((pg, idx) => {
              const gameId = getR64GameId(pg.destRegion, pg.destSeed);
              const slot = seedToSlot(pg.destSeed);
              const matchupPair = MATCHUP_PAIRS[seedToMatchupIdx(pg.destSeed)];

              return (
                <div key={pg.id} className="card">
                  <div className="card-header">
                    <h3 className="card-title" style={{ fontSize: '1.1rem' }}>First Four Game {idx + 1}</h3>
                    <span className="badge badge-orange">{pg.id}</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Teams with ESPN lookup */}
                    <div>
                      <label className="form-label">Teams Playing</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {['team1','team2'].map((teamField, tIdx) => {
                          const dropKey = `playin-${idx}-${teamField}`;
                          return (
                            <div key={teamField} style={{ position: 'relative' }}>
                              <input className="form-input"
                                placeholder={tIdx === 0 ? 'Team 1 (e.g. Alabama State)' : 'Team 2 (e.g. Saint Francis)'}
                                value={pg[teamField]}
                                onFocus={() => { loadEspnTeams(); setTeamDropdown(dropKey); setHighlightedIdx(-1); }}
                                onBlur={() => setTimeout(() => { setTeamDropdown(d => d === dropKey ? null : d); setHighlightedIdx(-1); }, 300)}
                                onKeyDown={e => {
                                  const matches = getEspnMatches(pg[teamField]);
                                  if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIdx(i => Math.min(i + 1, matches.length - 1)); }
                                  else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIdx(i => Math.max(i - 1, 0)); }
                                  else if (e.key === 'Tab' || e.key === 'Enter') {
                                    if (matches.length === 1) {
                                      updatePlayIn(idx, teamField, matches[0].name);
                                      updatePlayIn(idx, teamField + 'Id', matches[0].espnId);
                                      setTeamDropdown(null); setHighlightedIdx(-1);
                                      if (e.key === 'Enter') e.preventDefault();
                                    } else if (highlightedIdx >= 0 && matches[highlightedIdx]) {
                                      e.preventDefault();
                                      updatePlayIn(idx, teamField, matches[highlightedIdx].name);
                                      updatePlayIn(idx, teamField + 'Id', matches[highlightedIdx].espnId);
                                      setTeamDropdown(null); setHighlightedIdx(-1);
                                    }
                                  } else if (e.key === 'Escape') {
                                    setTeamDropdown(null); setHighlightedIdx(-1);
                                  }
                                }}
                                onChange={e => {
                                  updatePlayIn(idx, teamField, e.target.value);
                                  // also clear saved ESPN ID when name changes
                                  updatePlayIn(idx, teamField + 'Id', autoId(e.target.value));
                                }} />
                              {teamDropdown === dropKey && pg[teamField] && pg[teamField].length >= 2 && (
                                <div style={{
                                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                  background: 'var(--navy)', border: '1px solid var(--blue)',
                                  borderRadius: '0 0 8px 8px', maxHeight: 180, overflowY: 'auto',
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                                }}>
                                  {espnLoading && <div style={{ padding: '8px 12px', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.82rem' }}>Loading ESPN teams...</div>}
                                  {!espnLoading && getEspnMatches(pg[teamField]).map((t, tidx) => (
                                    <div key={t.espnId}
                                      onMouseDown={() => {
                                        updatePlayIn(idx, teamField, t.name);
                                        updatePlayIn(idx, teamField + 'Id', t.espnId);
                                        setTeamDropdown(null); setHighlightedIdx(-1);
                                      }}
                                      onMouseEnter={() => setHighlightedIdx(tidx)}
                                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--slate)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: highlightedIdx === tidx ? 'rgba(75,139,190,0.25)' : 'transparent' }}
                                    >
                                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', color: 'var(--cream)' }}>{t.name}</span>
                                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.75rem', color: 'var(--blue)' }}>ESPN: {t.espnId}</span>
                                    </div>
                                  ))}
                                  {!espnLoading && espnLoaded && getEspnMatches(pg[teamField]).length === 0 && (
                                    <div style={{ padding: '8px 12px', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.82rem' }}>No ESPN match found</div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div style={{ textAlign: 'center', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.82rem' }}>vs</div>
                      </div>
                    </div>

                    {/* Destination */}
                    <div>
                      <label className="form-label">Winner Advances To</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                          <label className="form-label" style={{ fontSize: '0.75rem' }}>Region</label>
                          <select className="form-select" value={pg.destRegion}
                            onChange={e => updatePlayIn(idx, 'destRegion', e.target.value)}>
                            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: '0.75rem' }}>As Seed #</label>
                          <select className="form-select" value={pg.destSeed}
                            onChange={e => updatePlayIn(idx, 'destSeed', Number(e.target.value))}>
                            {Array.from({length:16},(_,i)=>i+1).map(s => <option key={s} value={s}>{s} seed</option>)}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Auto-calculated result */}
                    {gameId && (
                      <div style={{ background: 'var(--navy)', border: '1px solid var(--blue)', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.72rem', color: 'var(--gray)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '1px' }}>
                          Auto-calculated R64 slot
                        </div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.95rem', color: 'var(--cream)' }}>
                          <span style={{ color: 'var(--blue)' }}>{gameId}</span>
                          {' — '}
                          {pg.destRegion} Region, Seed {matchupPair?.[0]} vs{' '}
                          <span style={{ color: 'var(--orange)', fontWeight: 700 }}>★ Play-In Winner (seed {pg.destSeed})</span>
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--gray)', marginTop: 4, fontFamily: 'var(--font-condensed)' }}>
                          Winner fills the {slot === 'home' ? 'top' : 'bottom'} ({slot}) slot
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setActiveTab('regionorder')}>← Region Order</button>
            <button className="btn btn-primary" onClick={() => setActiveTab('teams')}>Next: Enter Teams →</button>
          </div>
        </div>
      )}

      {/* ── 2. TEAMS ──────────────────────────────────────────── */}
      {activeTab === 'teams' && (
        <div>
          <p style={{ color: 'var(--gray)', marginBottom: 20, fontFamily: 'var(--font-condensed)' }}>
            Enter all 64 main-bracket teams by region and seed. Seeds that have Play-In games
            are shown as greyed-out — those slots are filled by Play-In winners automatically.
          </p>
          <div className="alert alert-info" style={{ marginBottom: 16, fontSize: '0.85rem' }}>
            Start typing a team name to search ESPN's database — selecting a match auto-fills the <strong>ESPN ID</strong> (green = numeric ESPN ID, used for live score matching). If a team isn't found, an ID is auto-generated but live scores <em>may not match</em> — you can manually enter the ESPN ID later.
          </div>
          {/* Seed entry order toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)' }}>Entry order:</span>
            <div style={{ display: 'flex', background: 'var(--navy-light)', border: '1px solid var(--slate)', borderRadius: 8, overflow: 'hidden' }}>
              <button
                onClick={() => setSeedEntryOrder('seed')}
                style={{
                  padding: '7px 16px', border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.5px',
                  background: seedEntryOrder === 'seed' ? 'var(--blue)' : 'transparent',
                  color: seedEntryOrder === 'seed' ? 'white' : 'var(--gray)',
                  transition: 'all 0.15s',
                }}
              >
                #1 → #16
              </button>
              <button
                onClick={() => setSeedEntryOrder('bracket')}
                style={{
                  padding: '7px 16px', border: 'none', borderLeft: '1px solid var(--slate)', cursor: 'pointer',
                  fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.5px',
                  background: seedEntryOrder === 'bracket' ? 'var(--blue)' : 'transparent',
                  color: seedEntryOrder === 'bracket' ? 'white' : 'var(--gray)',
                  transition: 'all 0.15s',
                }}
              >
                Bracket Order (1,16,8,9…)
              </button>
            </div>
            <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gray)' }}>
              {seedEntryOrder === 'bracket' ? '1v16 · 8v9 · 5v12 · 4v13 · 6v11 · 3v14 · 7v10 · 2v15' : 'Sequential seed order'}
            </span>
          </div>

          {/* Quadrant layout: Region 1=top-left, 2=top-right, 3=bottom-right, 4=bottom-left */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'auto auto', gap: 20 }}>
            {[regionOrder[0], regionOrder[1], regionOrder[3], regionOrder[2]].filter(Boolean).map((region, quadIdx) => {
              const overallSeed = regionOrder.indexOf(region) + 1;
              const quadLabels = ['Top Left', 'Top Right', 'Bottom Left', 'Bottom Right'];
              return (
              <div key={region} className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ fontSize: '1.3rem' }}>
                    {region.toUpperCase()}
                    <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gold)', marginLeft: 10 }}>#{overallSeed} Overall</span>
                  </h3>
                  <span style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.8rem' }}>
                    {quadLabels[quadIdx]} · {Object.entries(teamData[region]).filter(([seed, t]) => t.name && !playInSlotMap[region]?.has(Number(seed))).length} entered
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {(seedEntryOrder === 'bracket' ? BRACKET_ORDER : SEED_ORDER).map(seed => {
                    const isPlayIn = playInSlotMap[region]?.has(seed);
                    const pg = isPlayIn ? playInGames.find(p => p.destRegion === region && p.destSeed === seed) : null;
                    return (
                      <div key={seed} style={{ display: 'flex', gap: 8, alignItems: 'center', opacity: isPlayIn ? 0.5 : 1 }}>
                        <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--orange)', minWidth: 20, textAlign: 'right' }}>{seed}</span>
                        {isPlayIn ? <PlayInSlotLabel pg={pg} savedGames={savedGames} /> : (
                          <>
                            <div style={{ flex: 2, position: 'relative' }}>
                              <input className="form-input" placeholder={`Seed ${seed} team name`}
                                value={teamData[region][seed].name}
                                onFocus={() => { loadEspnTeams(); setTeamDropdown(`${region}-${seed}`); setHighlightedIdx(-1); }}
                                onBlur={() => setTimeout(() => { setTeamDropdown(d => d === `${region}-${seed}` ? null : d); setHighlightedIdx(-1); }, 300)}
                                onKeyDown={e => {
                                  const matches = getEspnMatches(teamData[region][seed].name);
                                  if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIdx(i => Math.min(i + 1, matches.length - 1)); }
                                  else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIdx(i => Math.max(i - 1, 0)); }
                                  else if (e.key === 'Tab' || e.key === 'Enter') {
                                    if (matches.length === 1) {
                                      // Auto-select sole match
                                      setTeam(region, seed, 'name', matches[0].name);
                                      setTeam(region, seed, 'id', matches[0].espnId);
                                      setTeamDropdown(null); setHighlightedIdx(-1);
                                      // Don't preventDefault for Tab so focus moves naturally
                                      if (e.key === 'Enter') e.preventDefault();
                                    } else if (highlightedIdx >= 0 && matches[highlightedIdx]) {
                                      e.preventDefault();
                                      setTeam(region, seed, 'name', matches[highlightedIdx].name);
                                      setTeam(region, seed, 'id', matches[highlightedIdx].espnId);
                                      setTeamDropdown(null); setHighlightedIdx(-1);
                                    }
                                  } else if (e.key === 'Escape') {
                                    setTeamDropdown(null); setHighlightedIdx(-1);
                                  }
                                }}
                                onChange={e => {
                                  const name = e.target.value;
                                  setTeam(region, seed, 'name', name);
                                  setTeamSearch(s => ({ ...s, [`${region}-${seed}`]: name }));
                                  if (!teamData[region][seed].id || teamData[region][seed].id === autoId(teamData[region][seed].name)) {
                                    setTeam(region, seed, 'id', autoId(name));
                                  }
                                }} />
                              {teamDropdown === `${region}-${seed}` && teamData[region][seed].name && teamData[region][seed].name.length >= 2 && (
                                <div style={{
                                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                  background: 'var(--navy)', border: '1px solid var(--blue)',
                                  borderRadius: '0 0 8px 8px', maxHeight: 200, overflowY: 'auto',
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                                }}>
                                  {espnLoading && (
                                    <div style={{ padding: '8px 12px', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.82rem' }}>
                                      Loading ESPN teams...
                                    </div>
                                  )}
                                  {!espnLoading && getEspnMatches(teamData[region][seed].name).map(t => (
                                    <div key={t.espnId}
                                      onMouseDown={() => {
                                        setTeam(region, seed, 'name', t.name);
                                        setTeam(region, seed, 'id', t.espnId);
                                        setTeamDropdown(null);
                                      }}
                                      style={{
                                        padding: '8px 12px', cursor: 'pointer',
                                        borderBottom: '1px solid var(--slate)',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(75,139,190,0.15)'}
                                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', color: 'var(--cream)' }}>{t.name}</span>
                                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.75rem', color: 'var(--blue)' }}>ESPN ID: {t.espnId}</span>
                                    </div>
                                  ))}
                                  {!espnLoading && espnLoaded && getEspnMatches(teamData[region][seed].name).length === 0 && (
                                    <div style={{ padding: '8px 12px', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.82rem' }}>
                                      No ESPN match — ID will be auto-generated
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <input className="form-input" placeholder="ESPN ID" style={{ flex: 1, fontSize: '0.78rem', color: teamData[region][seed].id && !isNaN(teamData[region][seed].id) ? 'var(--green)' : 'var(--gray)' }}
                              title="ESPN numeric ID — used for live score matching"
                              value={teamData[region][seed].id}
                              onChange={e => setTeam(region, seed, 'id', e.target.value)} />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
            })}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setActiveTab('playin')}>← Play-In Games</button>
            <button className="btn btn-primary" onClick={() => setActiveTab('finalfour')}>Next: Final Four →</button>
          </div>
        </div>
      )}

      {/* ── 3. REGION ORDER ─────────────────────────────────── */}
      {activeTab === 'regionorder' && (
        <div style={{ maxWidth: 560 }}>
          <div className="alert alert-info" style={{ marginBottom: 20 }}>
            Order regions by their <strong>overall seed ranking</strong> — Region 1 = #1 overall seed,
            Region 2 = #2 overall, etc. This sets the column order in the bracket picker and the
            2×2 quadrant layout for PDF printing. The 1-seed in each region will show their overall
            number in parentheses (e.g. <strong>UNC (#1)</strong>).
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">REGION ORDER</h3>
              <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)' }}>use ↑↓ to reorder</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {regionOrder.map((region, idx) => {
                const badgeColors = [
                  { bg: 'var(--gold)', text: 'var(--navy)' },
                  { bg: 'var(--gray)', text: 'var(--navy)' },
                  { bg: '#cd7f32', text: 'var(--navy)' },
                  { bg: 'var(--slate)', text: 'var(--cream)' },
                ];
                const badge = badgeColors[idx] || badgeColors[3];
                return (
                  <div key={region} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: 'var(--navy-light)',
                    border: `2px solid ${idx === 0 ? 'var(--gold)' : idx === 1 ? 'var(--gray)' : idx === 2 ? '#cd7f32' : 'var(--slate)'}`,
                    borderRadius: 10, padding: '12px 16px',
                  }}>
                    <div style={{
                      width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                      background: badge.bg, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      boxShadow: idx === 0 ? '0 0 12px rgba(241,196,15,0.4)' : 'none',
                    }}>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: badge.text, lineHeight: 1 }}>{idx + 1}</span>
                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.5rem', color: badge.text, letterSpacing: '0.5px', textTransform: 'uppercase' }}>OVR</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1.2rem', color: 'var(--cream)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                        {region}
                      </div>
                      <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gray)', marginTop: 2 }}>
                        #{idx + 1} overall seed · 1-seed shown as <span style={{ color: 'var(--gold)' }}>(#{idx + 1})</span> in bracket
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => moveRegion(idx, -1)} disabled={idx === 0} style={{
                        width: 34, height: 34, borderRadius: 6, border: '1px solid var(--slate)',
                        background: idx === 0 ? 'transparent' : 'var(--navy-mid)',
                        color: idx === 0 ? 'var(--slate)' : 'var(--cream)',
                        cursor: idx === 0 ? 'default' : 'pointer',
                        fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem',
                      }}>↑</button>
                      <button onClick={() => moveRegion(idx, 1)} disabled={idx === regionOrder.length - 1} style={{
                        width: 34, height: 34, borderRadius: 6, border: '1px solid var(--slate)',
                        background: idx === regionOrder.length - 1 ? 'transparent' : 'var(--navy-mid)',
                        color: idx === regionOrder.length - 1 ? 'var(--slate)' : 'var(--cream)',
                        cursor: idx === regionOrder.length - 1 ? 'default' : 'pointer',
                        fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem',
                      }}>↓</button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--navy)', borderRadius: 8, fontFamily: 'var(--font-condensed)', fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--gray)' }}>PDF layout: </span>
              <span style={{ color: 'var(--blue)' }}>{regionOrder[0]}</span>
              <span style={{ color: 'var(--gray)' }}> (top-left) · </span>
              <span style={{ color: 'var(--blue)' }}>{regionOrder[1]}</span>
              <span style={{ color: 'var(--gray)' }}> (top-right) · </span>
              <span style={{ color: 'var(--blue)' }}>{regionOrder[2]}</span>
              <span style={{ color: 'var(--gray)' }}> (bottom-right) · </span>
              <span style={{ color: 'var(--blue)' }}>{regionOrder[3]}</span>
              <span style={{ color: 'var(--gray)' }}> (bottom-left)</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => setActiveTab('playin')}>Next: Play-In Games →</button>
            <button className="btn btn-secondary" style={{ background: 'rgba(39,174,96,0.15)', borderColor: 'var(--green)', color: 'var(--green)' }}
              onClick={() => { setShowLoadModal(true); setLoadBracketPreview(null); setLoadBracketError(''); setJsonText(''); setJsonError(''); }}>
              📥 Load Bracket…
            </button>
            <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--green)', alignSelf: 'center' }}>
              ✓ Order saves automatically when you move regions
            </span>
          </div>

          {/* ── Load Bracket Modal ── */}
          {showLoadModal && (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: 20,
            }}>
              <div style={{
                background: 'var(--navy-mid)', border: '1px solid var(--slate)',
                borderRadius: 12, padding: 28, maxWidth: 560, width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                maxHeight: '90vh', overflowY: 'auto',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--blue)', letterSpacing: '2px', margin: 0 }}>
                    LOAD BRACKET
                  </h3>
                  <button onClick={() => { setShowLoadModal(false); setLoadBracketPreview(null); setLoadBracketError(''); setJsonText(''); setJsonError(''); }}
                    style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: '1.4rem' }}>✕</button>
                </div>

                {/* Mode switcher */}
                {!loadBracketPreview && (
                  <div className="tabs" style={{ marginBottom: 20 }}>
                    <button className={`tab${loadMode === 'espn' ? ' active' : ''}`} onClick={() => { setLoadMode('espn'); setLoadBracketError(''); }}>
                      🌐 ESPN API
                    </button>
                    <button className={`tab${loadMode === 'json' ? ' active' : ''}`} onClick={() => { setLoadMode('json'); setLoadBracketError(''); setJsonError(''); }}>
                      📋 JSON Import
                    </button>
                  </div>
                )}

                {/* ── ESPN Mode ── */}
                {!loadBracketPreview && loadMode === 'espn' && (
                  <>
                    <div className="alert alert-info" style={{ marginBottom: 16, fontSize: '0.85rem' }}>
                      📡 Fetches the <strong>current year's live tournament</strong> directly from ESPN after Selection Sunday.
                      For past years, use the <strong>JSON Import</strong> tab with a pre-built bracket file.
                    </div>
                    <div style={{ background: 'var(--navy)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontFamily: 'var(--font-condensed)', fontSize: '0.82rem' }}>
                      <span style={{ color: 'var(--gray)' }}>Region order: </span>
                      {regionOrder.map((r, i) => (
                        <span key={r}><span style={{ color: 'var(--blue)' }}>{r}</span>{i < 3 && <span style={{ color: 'var(--gray)' }}> → </span>}</span>
                      ))}
                      <div style={{ color: 'var(--gray)', marginTop: 4 }}>
                        Final Four: {regionOrder[0]} vs {regionOrder[3]} · {regionOrder[1]} vs {regionOrder[2]}
                      </div>
                    </div>
                    {existingSetup && (
                      <div className="alert alert-error" style={{ marginBottom: 16, fontSize: '0.85rem' }}>
                        ⚠️ <strong>Warning:</strong> Teams and games already exist. Loading will overwrite all existing bracket data.
                      </div>
                    )}
                    {loadBracketError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{loadBracketError}</div>}
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button className="btn btn-primary" onClick={fetchLoadBracketPreview} disabled={loadBracketRunning}
                        style={{ background: 'var(--green)', borderColor: 'var(--green)' }}>
                        {loadBracketRunning ? '⏳ Fetching from ESPN...' : `📡 Fetch ${new Date().getFullYear()} Bracket from ESPN`}
                      </button>
                      <button className="btn btn-secondary" onClick={() => { setShowLoadModal(false); setLoadBracketError(''); }}>Cancel</button>
                    </div>
                  </>
                )}

                {/* ── JSON Import Mode ── */}
                {!loadBracketPreview && loadMode === 'json' && (
                  <>
                    <div className="alert alert-info" style={{ marginBottom: 16, fontSize: '0.82rem' }}>
                      Paste bracket JSON or upload a <code>.json</code> file. Use the pre-built JSON files for past years, or create your own.
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                      <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '7px 14px', background: 'var(--navy-light)',
                        border: '1px solid var(--slate)', borderRadius: 8,
                        cursor: 'pointer', fontFamily: 'var(--font-condensed)',
                        fontSize: '0.85rem', color: 'var(--cream)',
                      }}>
                        📂 Upload .json file
                        <input type="file" accept=".json,application/json" style={{ display: 'none' }}
                          onChange={e => handleJsonFileUpload(e.target.files[0])} />
                      </label>
                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gray)' }}>— or paste JSON below —</span>
                    </div>
                    <textarea className="form-input" rows={10} value={jsonText}
                      onChange={e => { setJsonText(e.target.value); setJsonError(''); }}
                      placeholder={'{\n  "year": 2025,\n  "regions": {\n    "South": [{"seed": 1, "name": "Auburn"}, ...],\n    "East": [...],\n    "Midwest": [...],\n    "West": [...]\n  },\n  "playIn": [\n    {"seed": 16, "region": "South", "team1": "Alabama State", "team2": "Saint Francis"},\n    ...\n  ]\n}'}
                      style={{ fontFamily: 'monospace', fontSize: '0.78rem', resize: 'vertical', marginBottom: 12 }} />
                    {jsonError && <div className="alert alert-error" style={{ marginBottom: 12, fontSize: '0.82rem' }}>{jsonError}</div>}
                    {loadBracketError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{loadBracketError}</div>}
                    {existingSetup && (
                      <div className="alert alert-error" style={{ marginBottom: 12, fontSize: '0.85rem' }}>
                        ⚠️ <strong>Warning:</strong> Teams and games already exist and will be overwritten.
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button className="btn btn-primary" onClick={parseJsonBracket}
                        disabled={loadBracketRunning || !jsonText.trim()}
                        style={{ background: 'var(--green)', borderColor: 'var(--green)' }}>
                        {loadBracketRunning ? '⏳ Processing...' : '🔍 Preview Bracket'}
                      </button>
                      <button className="btn btn-secondary" onClick={() => { setShowLoadModal(false); setJsonText(''); setJsonError(''); }}>Cancel</button>
                    </div>
                  </>
                )}

                {/* Preview results — shared between ESPN and JSON modes */}
                {loadBracketPreview && (
                  <>
                    <div className="alert alert-success" style={{ marginBottom: 16 }}>
                      ✅ Found <strong>{loadBracketPreview.summary?.totalTeams} teams</strong> and <strong>{loadBracketPreview.summary?.totalGames} games</strong> for {loadBracketPreview.year}.
                      {loadBracketPreview.summary?.playInCount > 0 && ` Includes ${loadBracketPreview.summary.playInCount} First Four games.`}
                    </div>

                    {loadBracketPreview.unmatched?.length > 0 && (
                      <div className="alert alert-error" style={{ marginBottom: 16, fontSize: '0.82rem' }}>
                        ⚠️ <strong>{loadBracketPreview.unmatched.length} team(s) not matched to ESPN IDs</strong> — saved with placeholder IDs, fix manually in Teams tab:<br />
                        <span style={{ color: 'var(--gray)' }}>{loadBracketPreview.unmatched.join(', ')}</span>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                      {['South','East','Midwest','West'].map(r => (
                        <div key={r} style={{ background: 'var(--navy)', borderRadius: 8, padding: '8px 12px', fontFamily: 'var(--font-condensed)' }}>
                          <span style={{ color: 'var(--gray)', fontSize: '0.78rem' }}>{r}</span>
                          <span style={{ color: loadBracketPreview.summary?.regionCounts?.[r] > 0 ? 'var(--green)' : 'var(--orange)', fontWeight: 700, marginLeft: 8 }}>
                            {loadBracketPreview.summary?.regionCounts?.[r] || 0} teams
                          </span>
                        </div>
                      ))}
                    </div>

                    <details style={{ marginBottom: 12 }}>
                      <summary style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--blue)', cursor: 'pointer', marginBottom: 8, userSelect: 'none' }}>
                        👁 Review all teams
                      </summary>
                      <div style={{ maxHeight: 220, overflowY: 'auto', background: 'var(--navy)', borderRadius: 8, padding: '10px 12px' }}>
                        {['South','East','Midwest','West'].map(region => (
                          <div key={region} style={{ marginBottom: 10 }}>
                            <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.78rem', color: 'var(--gold)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 4 }}>{region}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {loadBracketPreview.teams?.filter(t => t.region === region).sort((a, b) => a.seed - b.seed).map(t => (
                                <span key={t.id + t.seed} style={{
                                  fontFamily: 'var(--font-condensed)', fontSize: '0.75rem', padding: '2px 8px', borderRadius: 4,
                                  background: t.id?.startsWith('unknown') ? 'rgba(231,76,60,0.2)' : 'rgba(75,139,190,0.15)',
                                  color: t.id?.startsWith('unknown') ? '#e74c3c' : 'var(--cream)',
                                  border: `1px solid ${t.id?.startsWith('unknown') ? 'rgba(231,76,60,0.4)' : 'var(--slate)'}`,
                                }}>
                                  ({t.seed}) {t.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>

                    <div style={{ background: 'var(--navy)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontFamily: 'var(--font-condensed)', fontSize: '0.82rem' }}>
                      <span style={{ color: 'var(--gray)' }}>Final Four wiring: </span>
                      <span style={{ color: 'var(--blue)' }}>{regionOrder[0]} vs {regionOrder[3]}</span>
                      <span style={{ color: 'var(--gray)' }}> · </span>
                      <span style={{ color: 'var(--blue)' }}>{regionOrder[1]} vs {regionOrder[2]}</span>
                    </div>

                    {loadBracketError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{loadBracketError}</div>}

                    <div style={{ display: 'flex', gap: 12 }}>
                      <button className="btn btn-primary" onClick={applyLoadBracket} disabled={loadBracketRunning}
                        style={{ background: 'var(--green)', borderColor: 'var(--green)' }}>
                        {loadBracketRunning ? '💾 Saving to Firestore...' : `✅ Save ${loadBracketPreview.year} Bracket to Firestore`}
                      </button>
                      <button className="btn btn-secondary" onClick={() => setLoadBracketPreview(null)} disabled={loadBracketRunning}>← Back</button>
                      <button className="btn btn-secondary" onClick={() => { setShowLoadModal(false); setLoadBracketPreview(null); setLoadBracketError(''); setJsonText(''); setJsonError(''); }} disabled={loadBracketRunning}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 4. FINAL FOUR ─────────────────────────────────────── */}
      {activeTab === 'finalfour' && (
        <div className="card" style={{ maxWidth: 680 }}>
          <div className="card-header"><h3 className="card-title">FINAL FOUR PAIRINGS</h3></div>
          <div className="alert alert-info" style={{ marginBottom: 20 }}>
            Final Four pairings are <strong>automatically set</strong> based on your Region Order:
            Region #1 always plays Region #4, and Region #2 always plays Region #3.
            The winners meet in the Championship.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Semifinal 1 (r4-1)', a: regionOrder[0], b: regionOrder[3], seeds: '1 vs 4' },
              { label: 'Semifinal 2 (r4-2)', a: regionOrder[1], b: regionOrder[2], seeds: '2 vs 3' },
            ].map(({ label, a, b, seeds }) => (
              <div key={label} style={{ background: 'var(--navy-light)', border: '1px solid var(--blue)', borderRadius: 10, padding: '14px 20px' }}>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>
                  {label} · Overall seeds {seeds}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1.2rem', color: 'var(--cream)', textTransform: 'uppercase' }}>{a}</span>
                  <span style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontWeight: 700 }}>vs</span>
                  <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1.2rem', color: 'var(--cream)', textTransform: 'uppercase' }}>{b}</span>
                </div>
              </div>
            ))}
            <div style={{ background: 'var(--navy-light)', border: '1px solid var(--slate)', borderRadius: 10, padding: '14px 20px' }}>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>Championship (final-1)</div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1.1rem', color: 'var(--gray)' }}>Semifinal 1 Winner vs Semifinal 2 Winner</div>
            </div>
          </div>
          <div style={{ padding: '10px 14px', background: 'rgba(75,139,190,0.1)', border: '1px solid var(--blue)', borderRadius: 8, marginBottom: 20, fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--gray)' }}>
            To change pairings, go back to <strong>Step 2: Region Order</strong> and reorder the regions.
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn btn-secondary" onClick={() => setActiveTab('teams')}>← Enter Teams</button>
            <button className="btn btn-primary" onClick={() => setActiveTab('verify')}>Next: Verify →</button>
          </div>
        </div>
      )}

      {/* ── 4. VERIFY & SAVE ──────────────────────────────────── */}
      {activeTab === 'verify' && (
        <div>
          <p style={{ color: 'var(--gray)', marginBottom: 20, fontFamily: 'var(--font-condensed)' }}>
            Save everything to Firestore then run the verification check.
            <strong> Do not open submissions until all checks pass.</strong>
          </p>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn btn-secondary" onClick={() => handleSaveAll(false)}>
              💾 {existingSetup ? 'Update' : 'Save'} Setup in Firestore
            </button>
            <button className="btn btn-secondary" onClick={handleVerify}>🔍 Run Verification</button>
            <button className="btn btn-secondary" onClick={handleResetR4} title="Reset r4-1, r4-2, final-1 game slots from wiring config">🔄 Reset FF Game Slots</button>
            {resetR4Status && <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--gray)' }}>{resetR4Status}</span>}
            {existingSetup && (
              <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--green)' }}>
                ✓ Setup data exists in Firestore
              </span>
            )}
          </div>

          {confirmOverwrite && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              <strong>⚠️ Setup data already exists!</strong> Saving will overwrite the current bracket structure.
              If the tournament has started and users have submitted entries, this could break scoring.
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button className="btn btn-danger" onClick={() => handleSaveAll(true)}>Yes, overwrite existing setup</button>
                <button className="btn btn-secondary" onClick={() => setConfirmOverwrite(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Play-In summary */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><h3 className="card-title" style={{ fontSize: '1rem' }}>PLAY-IN SUMMARY</h3></div>
            {playInGames.map((pg, idx) => (
              <div key={pg.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--slate)', fontFamily: 'var(--font-condensed)', fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--gray)' }}>Game {idx + 1}:</span>
                <span style={{ color: 'var(--cream)' }}>{pg.team1 || '?'} vs {pg.team2 || '?'}</span>
                <span>→ <span style={{ color: 'var(--blue)' }}>{pg.destRegion}</span> as seed <span style={{ color: 'var(--orange)' }}>{pg.destSeed}</span></span>
                <span style={{ color: 'var(--gray)', fontSize: '0.78rem' }}>{getR64GameId(pg.destRegion, pg.destSeed)}</span>
              </div>
            ))}
          </div>

          {verifyResult && (
            <div>
              <div className={`alert ${verifyResult.valid ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 16 }}>
                {verifyResult.valid
                  ? '✅ All checks passed! Bracket is ready — you can open submissions.'
                  : `❌ ${verifyResult.errors.length} error${verifyResult.errors.length !== 1 ? 's' : ''} found. Fix before opening submissions.`}
              </div>
              {verifyResult.errors.length > 0 && (
                <div className="card" style={{ marginBottom: 16 }}>
                  <div className="card-header"><h3 className="card-title" style={{ fontSize: '1.1rem', color: 'var(--red)' }}>ERRORS</h3></div>
                  {verifyResult.errors.map((e, i) => (
                    <div key={i} style={{ padding: '7px 0', borderBottom: '1px solid var(--slate)', color: 'var(--red)', fontFamily: 'var(--font-condensed)', fontSize: '0.9rem' }}>✗ {e}</div>
                  ))}
                </div>
              )}
              <div className="card">
                <div className="card-header"><h3 className="card-title" style={{ fontSize: '1.1rem' }}>SUMMARY</h3></div>
                <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                  <div className="stat-box"><div className="stat-value">{verifyResult.summary.totalTeams}</div><div className="stat-label">Teams</div></div>
                  <div className="stat-box"><div className="stat-value">{verifyResult.summary.totalGames}</div><div className="stat-label">Games</div></div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '1px' }}>Teams per Region</div>
                  {REGIONS.map(r => (
                    <div key={r} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--slate)', fontFamily: 'var(--font-condensed)' }}>
                      <span>{r}</span>
                      <span style={{ color: verifyResult.summary.regionTeamCounts?.[r] === 16 ? 'var(--green)' : 'var(--red)' }}>
                        {verifyResult.summary.regionTeamCounts?.[r] || 0}/16 {verifyResult.summary.regionTeamCounts?.[r] === 16 ? '✓' : ''}
                      </span>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '1px' }}>Final Four Matchups</div>
                  {(verifyResult.summary.finalFourMatchups || []).map(m => (
                    <div key={m.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--slate)', fontFamily: 'var(--font-condensed)' }}>
                      <span style={{ color: 'var(--orange)' }}>{m.id}: </span>{m.home} vs {m.away}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 6. ESPN AUTO-SEED ─────────────────────────────────── */}
      {activeTab === 'espn' && (
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <h3 className="card-title">ESPN AUTO-SEED</h3>
              <span className="badge badge-orange">Use after Selection Sunday</span>
            </div>
            <div className="alert alert-info" style={{ marginBottom: 20 }}>
              Fetches the full NCAA tournament bracket from ESPN after Selection Sunday.
              Creates all 68 teams and 67 games automatically. You can still manually
              edit any game or team afterwards in the Games tab.
            </div>
            {espnSeedError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{espnSeedError}</div>}
            {espnSeedStatus && <div className="alert alert-success" style={{ marginBottom: 16 }}>{espnSeedStatus}</div>}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <button className="btn btn-primary" onClick={fetchEspnBracket} disabled={espnSeeding}>
                {espnSeeding ? 'Fetching from ESPN...' : 'Fetch 2026 Bracket from ESPN'}
              </button>
              {espnPreview && (
                <button className="btn btn-primary" onClick={applyEspnSeed} disabled={espnSeeding}
                  style={{ background: 'var(--green)', borderColor: 'var(--green)' }}>
                  Save to Firestore ({espnPreview.teams?.length} teams, {espnPreview.games?.length} games)
                </button>
              )}
            </div>
            {espnPreview && (
              <div>
                <div className="grid-4" style={{ marginBottom: 16 }}>
                  {['South','East','Midwest','West'].map(r => (
                    <div key={r} className="stat-box" style={{ textAlign: 'center' }}>
                      <div className="stat-value" style={{ fontSize: '1.8rem' }}>
                        {espnPreview.teams?.filter(t => t.region === r).length || 0}
                      </div>
                      <div className="stat-label">{r}</div>
                    </div>
                  ))}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="leaderboard-table">
                    <thead><tr><th>Seed</th><th>Team</th><th>Region</th><th>ESPN ID</th></tr></thead>
                    <tbody>
                      {espnPreview.teams?.sort((a,b) => (a.region+a.seed).localeCompare(b.region+b.seed)).map(t => (
                        <tr key={t.id}>
                          <td style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700 }}>({t.seed})</td>
                          <td style={{ fontFamily: 'var(--font-condensed)' }}>{t.name}</td>
                          <td><span className="badge badge-orange">{t.region}</span></td>
                          <td style={{ color: 'var(--gray)', fontSize: '0.82rem' }}>{t.id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 7. TOOLS ──────────────────────────────────────────── */}
      {activeTab === 'tools' && (
        <div>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">FIX SLEEPER IDS</h3>
              <span className="badge badge-orange">One-time migration</span>
            </div>
            <div className="alert alert-info" style={{ marginBottom: 20 }}>
              Some brackets may have a slug (e.g. <code>colorado-buffaloes</code>) stored as their
              sleeper team ID instead of an ESPN numeric ID (e.g. <code>38</code>). This tool scans
              all brackets, finds mismatches, and updates them to the correct ESPN ID so sleeper
              bonus points score correctly.
            </div>
            {migrationError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{migrationError}</div>}

            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <button className="btn btn-secondary" onClick={() => runSleeperMigration(true)} disabled={migrationRunning}>
                {migrationRunning ? 'Scanning...' : '🔍 Dry Run (preview only, no changes)'}
              </button>
              {migrationResult?.dryRun && migrationResult?.affected > 0 && (
                <button className="btn btn-primary" onClick={() => runSleeperMigration(false)} disabled={migrationRunning}
                  style={{ background: 'var(--green)', borderColor: 'var(--green)' }}>
                  ✓ Apply Fix ({migrationResult.affected} bracket{migrationResult.affected !== 1 ? 's' : ''})
                </button>
              )}
              {migrationResult && !migrationResult.dryRun && (
                <button className="btn btn-secondary" onClick={() => runSleeperMigration(true)} disabled={migrationRunning}>
                  Re-scan
                </button>
              )}
            </div>

            {migrationResult && (
              <div>
                <div className={`alert ${migrationResult.affected === 0 ? 'alert-success' : migrationResult.dryRun ? 'alert-info' : 'alert-success'}`}
                  style={{ marginBottom: 16 }}>
                  {migrationResult.dryRun
                    ? (migrationResult.affected === 0 && migrationResult.gamesAffected === 0)
                      ? '✅ All brackets and games have valid ESPN IDs — no fixes needed!'
                      : `Found ${migrationResult.affected} bracket${migrationResult.affected !== 1 ? 's' : ''} and ${migrationResult.gamesAffected || 0} game${migrationResult.gamesAffected !== 1 ? 's' : ''} with slug-based IDs. Click Apply Fix to correct them.`
                    : `✅ Fixed ${migrationResult.fixed} bracket${migrationResult.fixed !== 1 ? 's' : ''} and ${migrationResult.gamesFixed || 0} game${migrationResult.gamesFixed !== 1 ? 's' : ''} successfully.`
                  }
                </div>
                {migrationResult.details?.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="leaderboard-table">
                      <thead>
                        <tr>
                          <th>Bracket</th>
                          <th>User</th>
                          <th>Old Sleeper ID</th>
                          <th>New ESPN ID</th>
                          <th>Team Name</th>
                          <th style={{ textAlign: 'center' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {migrationResult.details.map((d, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>{d.bracketName}</td>
                            <td style={{ color: 'var(--gray)' }}>{d.userName}</td>
                            <td style={{ color: 'var(--red)', fontFamily: 'monospace', fontSize: '0.85rem' }}>{d.oldId}</td>
                            <td style={{ color: 'var(--green)', fontFamily: 'monospace', fontSize: '0.85rem' }}>{d.newId || '?'}</td>
                            <td style={{ color: 'var(--gray)' }}>{d.teamName || 'Unknown'}</td>
                            <td style={{ textAlign: 'center' }}>
                              {d.newId
                                ? <span className="badge badge-green">Fixable</span>
                                : <span className="badge badge-red">No match found</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 8. ESPN TEAMS CACHE ───────────────────────────────── */}
      {activeTab === 'espncache' && (
        <div>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">ESPN TEAMS CACHE</h3>
              <span className="badge badge-orange">All D1 Teams</span>
            </div>
            <div className="alert alert-info" style={{ marginBottom: 16 }}>
              Caches all ESPN D1 basketball team names and IDs in Firestore so the app
              can resolve team names without hitting ESPN at runtime. Update once per year
              after rosters/teams change, or any time a team name lookup looks wrong.
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
              <div className="stat-box" style={{ textAlign: 'center', minWidth: 120 }}>
                <div className="stat-value">{espnCacheTeams.length || espnCacheStatus?.count || 0}</div>
                <div className="stat-label">Cached Teams</div>
              </div>
              <div className="stat-box" style={{ textAlign: 'center', minWidth: 180 }}>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', marginBottom: 4 }}>Last Updated</div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', color: 'var(--cream)' }}>
                  {espnCacheStatus?.updatedAt ? new Date(espnCacheStatus.updatedAt).toLocaleString() : 'Never'}
                </div>
              </div>
              <button className="btn btn-primary" disabled={espnCacheUpdating}
                onClick={async () => {
                  setEspnCacheUpdating(true); setEspnCacheMsg('');
                  try {
                    const result = await api.updateEspnTeamsCache();
                    setEspnCacheMsg(`✅ Cached ${result.count} teams successfully.`);
                    setEspnCacheStatus(prev => ({ ...prev, count: result.count, updatedAt: new Date().toISOString() }));
                    setEspnCacheTeams([]);
                    loadEspnCacheTeams();
                  } catch (e) { setEspnCacheMsg('❌ ' + e.message); }
                  finally { setEspnCacheUpdating(false); }
                }}>
                {espnCacheUpdating ? '⏳ Fetching from ESPN...' : `${espnCacheStatus?.count ? '🔄 Update' : '🏗 Build'} ESPN Teams Cache`}
              </button>
            </div>

            {espnCacheMsg && (
              <div className={`alert ${espnCacheMsg.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 16 }}>
                {espnCacheMsg}
              </div>
            )}

            {espnCacheTeams.length > 0 ? (
              <>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                  <input className="form-input" type="text" placeholder="Search by name or ID..."
                    value={espnCacheSearch} onChange={e => setEspnCacheSearch(e.target.value)} style={{ maxWidth: 280 }} />
                  <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--gray)' }}>
                    {espnCacheTeams.filter(t => !espnCacheSearch || t.name.toLowerCase().includes(espnCacheSearch.toLowerCase()) || t.id.includes(espnCacheSearch) || t.abbrev.toLowerCase().includes(espnCacheSearch.toLowerCase())).length} teams
                  </span>
                </div>
                <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        {[['name','Team Name'],['abbrev','Abbrev'],['id','ESPN ID']].map(([col, label]) => (
                          <th key={col} onClick={() => setEspnCacheSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }))}
                            style={{ cursor: 'pointer', userSelect: 'none' }}>
                            {label} {espnCacheSort.col === col ? (espnCacheSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {espnCacheTeams
                        .filter(t => !espnCacheSearch || t.name.toLowerCase().includes(espnCacheSearch.toLowerCase()) || t.id.includes(espnCacheSearch) || t.abbrev.toLowerCase().includes(espnCacheSearch.toLowerCase()))
                        .sort((a, b) => {
                          const dir = espnCacheSort.dir === 'asc' ? 1 : -1;
                          const av = a[espnCacheSort.col] || '', bv = b[espnCacheSort.col] || '';
                          return espnCacheSort.col === 'id' ? dir * (parseInt(av) - parseInt(bv)) : dir * av.localeCompare(bv);
                        })
                        .map(t => (
                          <tr key={t.id}>
                            <td style={{ fontFamily: 'var(--font-condensed)' }}>{t.name}</td>
                            <td style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)' }}>{t.abbrev}</td>
                            <td style={{ color: 'var(--blue)', fontFamily: 'monospace', fontSize: '0.85rem' }}>{t.id}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <button className="btn btn-secondary" onClick={loadEspnCacheTeams} style={{ marginTop: 8 }}>
                📋 Load Team List
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
